// The six timeline track colours.
//
// A LEAF MODULE on purpose: dashboardTheme.js needs these to publish the
// `--color-clip-*` vars, and editorTheme.js needs the dark var set to pin the
// editor's theme. Putting the colours in either file makes those two import
// each other, and the cycle resolves to `undefined` at module-init time —
// which is a silent, confusing failure rather than a build error.
/**
 * The six track colours, one per timeline lane.
 *
 * Exported separately from `editorVars` because these live in
 * dashboardTheme.js's var sets (so Tailwind can reach them through
 * `--color-clip-*-rgb`), while the surfaces above are applied per-route. This
 * object is the single source of truth both of those read from, and what
 * check-contrast.mjs asserts against.
 *
 * Each carries an `rgb` comma triplet as well as a hex, and BOTH are required.
 * Tailwind's colour entries use the legacy `rgba(var(--x-rgb), <alpha-value>)`
 * form; a plain `var(--x)` string cannot take an opacity modifier at all, so
 * `bg-clipVideo/55` would silently render fully opaque. See the block comment
 * over volt/signal in tailwind.config.js — this exact mistake "broke volt
 * everywhere on the first try".
 *
 * Solved against three constraints at once, not picked by eye:
 *   1. the colour reads on its own (a 3px rail, a swatch) at AA on every lane;
 *   2. its 55% fill still carries a bone label at AA;
 *   3. every pair is separable either by lightness OR by hue.
 *
 * (1) and (2) pull against each other — brighter reads better solo but its
 * fill goes too pale for white text — which is why the viable band per hue is
 * narrow and the six sit on a deliberate luminance ladder from video (darkest)
 * to text (lightest). Worst hue gap is 31 degrees, worst luminance ratio 1.02;
 * see check-contrast.mjs for why both are checked rather than luminance alone.
 * (Those two worst cases are on DIFFERENT pairs — every pair clears one bar or
 * the other, which is exactly what the either/or rule is for.)
 *
 * `footage` was solved the same way rather than picked. The obvious candidate
 * was FILM's cyan #5AC8E8, already in the palette — it FAILS rule (2) at
 * 4.37:1 on the track bed, being too light for a 55% fill to carry a label.
 * #3FBFD4 sits in the one wide hue gap left (~188 degrees, between text at 150
 * and video at 220) and clears all three.
 */
export const clipColors = {
  footage: { hex: "#3FBFD4", rgb: "63, 191, 212" },
  video: { hex: "#5081E2", rgb: "80, 129, 226" },
  sfx: { hex: "#EDA221", rgb: "237, 162, 33" },
  text: { hex: "#06DB70", rgb: "6, 219, 112" },
  sub: { hex: "#BA71EF", rgb: "186, 113, 239" },
  music: { hex: "#E981AC", rgb: "233, 129, 172" },
};

/** The clip vars, in the shape dashboardTheme.js's var objects want. */
export const clipVars = Object.fromEntries(
  Object.entries(clipColors).flatMap(([name, { hex, rgb }]) => [
    [`--color-clip-${name}`, hex],
    [`--color-clip-${name}-rgb`, rgb],
  ])
);

/**
 * The alpha the timeline fills clips at. Exported so check-contrast.mjs
 * asserts the label contrast at the SAME value the UI actually renders —
 * hardcoding 0.55 in both places is how the check drifts from the thing it
 * is supposed to be checking.
 */
export const CLIP_FILL_ALPHA = 0.55;
